import { DataLayerAction } from '@corratech/pylot-tag-manager/src/TagContext.d'
import { useCart } from '@corratech/pylot-cart-manager'
import { useObjectCompare } from '@components/OOKA/pylot-tag-manager/src/hooks/useObjectCompare'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Cart } from '@pylot-data/pylotschema'
import { identifyCartOperationName } from '@corratech/pylot-tag-manager/src/TrackCartState/identifyCartOperationName'
import { identifyPDPCartOperationName } from 'stores/ooka_usa_store/components/OOKA/pylot-tag-manager/src/TrackCartState/identifyPDPCartOperationName'
import { identifyCartProductsCategories } from '@corratech/pylot-tag-manager/src/TrackCartState/identifyCartProductsCategories'
import { identifyUpdatedItems } from '@corratech/pylot-tag-manager/src/TrackCartState/identifyUpdatedItems'
import stringify from 'fast-json-stable-stringify'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

export const TrackCartState = ({
    dataLayerAction
}: {
    dataLayerAction: DataLayerAction
}): null => {
    const {
        isFallback,
        query: { categoryName, url_path }
    } = useRouter()
    const trackedCartState = useRef<Cart | undefined>()
    const { data } = useCart({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    })
    const cart = data?.data?.cart
    const isOptimistic = data?.data?.isOptimistic
    const hasChanges = useObjectCompare({}, ['items', 'prices'])

    // @ts-ignore
    const { gtmLocation, setGTMLocation } = useAlfakherContext()

    useEffect(() => {
        // @ts-ignore
        const lastThreeEvents = window.dataLayer?.slice(-3) ?? []
        const purchaseEvent = lastThreeEvents.find(
            (item: any) => item[1] === 'purchase'
        )
        const gtmLocations = ['RELATED', 'UPSELL', 'CROSSSELL']

        if (
            cart &&
            !isFallback &&
            !isOptimistic &&
            hasChanges(cart, true) &&
            !purchaseEvent
        ) {
            const updatedItems = identifyUpdatedItems(
                cart,
                trackedCartState.current
            )

            dataLayerAction({
                type: identifyCartOperationName(cart, trackedCartState.current),
                data: {
                    ...cart,
                    updatedItems: identifyCartProductsCategories(
                        updatedItems,
                        url_path,
                        categoryName
                    )
                }
            })

            if (gtmLocations.includes(gtmLocation)) {
                if (gtmLocation === 'RELATED' && Array.isArray(updatedItems)) {
                    updatedItems.shift()
                    if (updatedItems.length < 1) {
                        return
                    }
                }
                dataLayerAction({
                    type: identifyPDPCartOperationName(
                        cart,
                        trackedCartState.current,
                        gtmLocation
                    ),
                    data: {
                        ...cart,
                        updatedItems: identifyCartProductsCategories(
                            updatedItems,
                            url_path,
                            categoryName
                        ),
                        eventLocation: gtmLocation
                    }
                })
                setGTMLocation('')
            }

            // Saved cart state needs to be stripped of internal references to the current state
            trackedCartState.current = JSON.parse(
                // @ts-ignore - options are supported but not declared in the definition
                stringify(cart, { cycles: true })
            )
        }
    }, [cart, isFallback, isOptimistic])

    return null
}
